import { useMutation, useQuery } from "@apollo/client/react";
import { throwForValidationResult } from "@/lib/graphql-error";
import { useIntl, type IntlShape } from "react-intl";
import {
  CreateCertificateDocument,
  DeleteCertificateDocument,
  MyselfExpertiseDataDocument,
  UpdateCertificateDocument,
  type MyselfExpertiseDataQuery,
} from "@/graphql/generated/operations";
import { audienceRelations, type Audience } from "@/lib/audience";
import { toDateInstant } from "@/lib/date-instant";

export interface CertificateFormInput {
  name: string;
  date: string;
  sharedWith?: ReadonlyArray<Audience | string>;
}

export type CertificateItem = NonNullable<
  NonNullable<MyselfExpertiseDataQuery["myself"]>["certifications"]
>[number];

export interface UseCertificatesResult {
  items: readonly CertificateItem[];
  add: (input: CertificateFormInput) => Promise<void>;
  update: (id: string, input: CertificateFormInput) => Promise<void>;
  remove: (id: string) => Promise<void>;
  busy: boolean;
  error: Error | null;
}

function toDataInput(input: CertificateFormInput, intl: IntlShape) {
  if (!input.name.trim()) {
    throw new Error(
      intl.formatMessage({ id: "profile.page.error.certificateNameRequired", defaultMessage: "Name is required." }),
    );
  }
  return {
    name: input.name,
    date: toDateInstant(input.date),
    ...(input.sharedWith !== undefined
      ? { sharedWith: audienceRelations(input.sharedWith) }
      : {}),
  };
}

export function useCertificates(): UseCertificatesResult {
  const intl = useIntl();
  const query = useQuery(MyselfExpertiseDataDocument);
  const personId = query.data?.myself?.id;

  const saveFailed = intl.formatMessage({ id: "profile.page.error.saveCertificate", defaultMessage: "Could not save the certificate" });

  const [createMut, createState] = useMutation(CreateCertificateDocument, {
    refetchQueries: [{ query: MyselfExpertiseDataDocument }],
  });
  const [updateMut, updateState] = useMutation(UpdateCertificateDocument, {
    refetchQueries: [{ query: MyselfExpertiseDataDocument }],
  });
  const [deleteMut, deleteState] = useMutation(DeleteCertificateDocument, {
    refetchQueries: [{ query: MyselfExpertiseDataDocument }],
  });

  async function add(input: CertificateFormInput) {
    if (!personId) throw new Error("useCertificates: no personId yet");
    const created = await createMut({
      variables: {
        input: { data: { ...toDataInput(input, intl), person: { id: personId } } },
      },
    });
    // Both union members are selected, so a rejection resolves rather than throws. Fail-closed on
    // the success typename so an unexpected member is a failure, not a silent "saved".
    const out = created.data?.createCertificate;
    if (out?.__typename !== "CreateCertificateResult") {
      throwForValidationResult(out, saveFailed);
    }
  }

  async function update(id: string, input: CertificateFormInput) {
    const updated = await updateMut({ variables: { id, input: { data: toDataInput(input, intl) } } });
    // Both union members are selected, so a rejection resolves rather than throws. Fail-closed on
    // the success typename so an unexpected member is a failure, not a silent "saved".
    const out = updated.data?.updateCertificate;
    if (out?.__typename !== "UpdateCertificateResult") {
      throwForValidationResult(out, saveFailed);
    }
  }

  async function remove(id: string) {
    await deleteMut({ variables: { input: { id } } });
  }

  return {
    items: query.data?.myself?.certifications ?? [],
    add,
    update,
    remove,
    busy: createState.loading || updateState.loading || deleteState.loading,
    error:
      (createState.error as Error | undefined) ??
      (updateState.error as Error | undefined) ??
      (deleteState.error as Error | undefined) ??
      null,
  };
}
